import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { portalWithAppContext } from 'components/organisms/app-context/portal-with-context';
import { RlGroupSectionName } from '../custom-group-section/extension';
import { RlCustomSectionName } from '../custom-section/extension';
import { SectionMenuTrigger } from './ui/section-menu-trigger';

export interface SectionMenuOptions {
  enabled: boolean;
  documentAgentId?: string;
  aiContextsData?: any;
  onSectionClick?: (nodeData: any) => void;
}

const SectionMenuPluginKey = new PluginKey('sectionMenu');

/** Meta key: when set on a transaction, decorations are recomputed (e.g. after AI context update). */
export const SECTION_MENU_REFRESH_META = 'sectionMenuRefresh';
/** Meta key: optional fresh aiContextsData when dispatching section menu refresh. */
export const SECTION_MENU_AI_CONTEXTS_META = 'aiContextsData';

export const SectionMenu = Extension.create<SectionMenuOptions>({
  name: 'sectionMenu',

  addOptions() {
    return {
      enabled: true,
      documentAgentId: undefined,
      aiContextsData: undefined,
      onSectionClick: undefined,
    };
  },

  addProseMirrorPlugins() {
    const editor = this.editor;
    const options = this.options;

    const createDecorations = (doc: any, aiContextsDataOverride?: any) => {
      const decorations: Decoration[] = [];
      const aiContextsData = aiContextsDataOverride ?? options.aiContextsData;

      doc.descendants((node: any, pos: number) => {
        const isCustomSection = node.type.name === RlCustomSectionName;
        const isGroupSection = node.type.name === RlGroupSectionName;

        if (isCustomSection || isGroupSection) {
          const isInsideGroupSection =
            isCustomSection &&
            doc.resolve(pos).parent?.type?.name === RlGroupSectionName;

          if (isInsideGroupSection) {
            return;
          }

          const sectionId =
            node.attrs.templateSectionId || node.attrs.documentSectionId || '';

          const hasAiContext = (): boolean => {
            if (!aiContextsData) {
              return false;
            }

            if (aiContextsData[sectionId]) {
              return true;
            }

            if (isGroupSection && node.content) {
              let found = false;

              node.content.forEach((child: any) => {
                if (child.type.name === RlCustomSectionName) {
                  const childId =
                    child.attrs?.templateSectionId ||
                    child.attrs?.documentSectionId ||
                    '';

                  if (childId && aiContextsData[childId]) {
                    found = true;
                  }
                }
              });

              return found;
            }

            return false;
          };

          if (!hasAiContext()) {
            return;
          }

          const decoration = Decoration.widget(
            pos + 1, // Place at the start of the section
            () => {
              const container = document.createElement('div');

              container.className = 'section-menu-trigger-container';
              container.setAttribute('data-section-pos', pos.toString());
              container.setAttribute('data-section-id', sectionId);

              portalWithAppContext({
                container,
                children: (
                  <SectionMenuTrigger
                    editor={editor}
                    sectionPos={pos}
                    nodeData={{
                      id: sectionId,
                      ...node.attrs,
                      type: node.type.name,
                    }}
                    documentAgentId={options.documentAgentId}
                    onSectionClick={options.onSectionClick}
                  />
                ),
              });

              return container;
            },
            {
              side: -1, // Place before the position
              key: `section-menu-${pos}`,
            },
          );

          decorations.push(decoration);
        }
      });

      return DecorationSet.create(doc, decorations);
    };

    return [
      new Plugin({
        key: SectionMenuPluginKey,
        state: {
          init: (_, state) => {
            return createDecorations(state.doc);
          },
          apply: (tr, oldState) => {
            if (tr.getMeta(SECTION_MENU_REFRESH_META)) {
              const freshData = tr.getMeta(SECTION_MENU_AI_CONTEXTS_META);

              return createDecorations(tr.doc, freshData);
            }

            if (!tr.docChanged) {
              return oldState;
            }

            return createDecorations(tr.doc);
          },
        },
        props: {
          decorations(state) {
            return SectionMenuPluginKey.getState(state);
          },
        },
      }),
    ];
  },
});
